import React from "react";

import { FitBadge, withLiveDeadline } from "./CopilotTenderResultsCard";
import { formatDaysRemaining } from "../../../services/tendersApi";

/* The screen before a conversation exists.
 *
 * Its only job is getting someone past the blank page. Three things do that:
 * a greeting that proves the tool knows who and when you are, prompts concrete
 * enough to send unedited, and an entrance that makes the page feel alive.
 */

const Icon = ({ children }) => (
  <svg aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

/* Phrased the way a bid manager would actually ask, not as feature names. Each
   is complete enough to send as-is — a prompt you still have to finish writing
   is just the blank page with extra steps. */
const SUGGESTIONS = [
  {
    kind: "Review",
    label: "Summarise this RFP and list every requirement we must answer",
    icon: (
      <Icon>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="8" y1="13" x2="16" y2="13" />
        <line x1="8" y1="17" x2="13" y2="17" />
      </Icon>
    ),
  },
  {
    kind: "Decide",
    label: "Should we bid on this? Score it and show your reasoning",
    icon: (
      <Icon>
        <circle cx="12" cy="12" r="3.2" />
        <path d="M12 2v3M12 19v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1L7 17M17 7l2.1-2.1" />
      </Icon>
    ),
  },
  {
    kind: "Draft",
    label: "Draft the technical approach using our knowledge base",
    icon: (
      <Icon>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
      </Icon>
    ),
  },
];

const firstName = (name) => {
  const raw = String(name || "").trim();
  if (!raw) return "";
  const f = raw.split(/\s+/)[0];
  return f.charAt(0).toUpperCase() + f.slice(1);
};

/* Tenders Discover found for this workspace overnight — the three best, so the
   first screen of the day answers "anything new worth bidding on?" without a
   question. Nothing renders until there is something to show. */
const PickedForYou = ({ picks, onOpen, onSeeAll }) => {
  const rows = picks?.results || [];
  if (!rows.length) return null;
  const fresh = picks.stats?.new || 0;
  return (
    <div
      className="relative z-10 mt-8 w-full animate-copilot-rise motion-reduce:animate-none"
      style={{ animationDelay: "520ms" }}
    >
      <div className="mb-2 flex items-center justify-between px-1">
        <p className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.08em] text-[#77707E]">
          Picked for you
          {fresh > 0 && (
            <span className="rounded-full bg-[#A04A97] px-2 py-[1px] text-[11px] font-bold normal-case tracking-normal text-white">
              {fresh} new
            </span>
          )}
        </p>
        <button
          type="button"
          onClick={onSeeAll}
          className="text-[13.5px] font-semibold text-[#A04A97] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
        >
          See all in Discover →
        </button>
      </div>
      <div className="overflow-hidden rounded-[14px] border border-[#A04A97]/15 bg-white/80 backdrop-blur-[8px]">
        {rows.map((row, i) => {
          const t = withLiveDeadline(row);
          return (
            <button
              key={row.id}
              type="button"
              data-sensa-keep-panel
              onClick={() => onOpen?.(row)}
              className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[#A04A97]/[0.05] focus:outline-none focus-visible:bg-[#A04A97]/[0.07] ${
                i ? "border-t border-[#18101C]/[0.06]" : ""
              }`}
            >
              <FitBadge band={row.match?.band || row.fit?.band} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14.5px] font-medium text-[#18101C]">{t.title}</span>
                <span className="block truncate text-[12.5px] text-[#77707E]">
                  {row.match?.why?.[0]?.text || t.buyer}
                </span>
              </span>
              <span className="flex-none text-[12.5px] font-medium text-[#52525B]">{formatDaysRemaining(t)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const CopilotEmptyState = ({ greeting, displayName, onPick, picks, onOpenPick, onSeeAllPicks }) => {
  const name = firstName(displayName);

  return (
    <div className="relative w-full max-w-[780px] flex flex-col items-center py-10">
      <div className="relative z-10 text-center">
        <p
          className="text-[16px] font-medium text-[#77707E] animate-copilot-rise motion-reduce:animate-none"
          style={{ animationDelay: "40ms" }}
        >
          {greeting}
          {name ? `, ${name}` : ""}
        </p>
        <h1
          className="mt-1.5 text-[clamp(28px,3.4vw,40px)] font-bold leading-[1.12] tracking-[-0.02em] text-[#18101C] text-balance animate-copilot-rise motion-reduce:animate-none"
          style={{ animationDelay: "110ms" }}
        >
          What are we{" "}
          {/* The gradient walks across the word instead of anything moving —
              motion the eye catches with no layout shift. */}
          <span className="bg-gradient-to-r from-[#A04A97] via-[#F3904F] to-[#A04A97] bg-[length:200%_auto] bg-clip-text text-transparent animate-copilot-aurora motion-reduce:animate-none">
            winning
          </span>{" "}
          today?
        </h1>
        <p
          className="mt-2.5 text-[15.5px] text-[#5B5563] animate-copilot-rise motion-reduce:animate-none"
          style={{ animationDelay: "180ms" }}
        >
          Ask anything about your RFPs, projects or knowledge base.
        </p>
      </div>

      <div className="relative z-10 mt-9 grid w-full grid-cols-1 gap-3.5 md:grid-cols-3">
        {SUGGESTIONS.map((s, i) => (
          <button
            key={s.kind}
            type="button"
            onClick={() => onPick?.(s.label)}
            // Cards land in sequence so the eye is led across them rather than
            // hit with three at once.
            style={{ animationDelay: `${250 + i * 80}ms` }}
            className="group/card relative flex flex-col gap-2.5 overflow-hidden rounded-[14px] border border-[#A04A97]/15 bg-white/75 p-4 text-left shadow-[0_1px_2px_rgba(24,16,28,0.04),0_10px_26px_-20px_rgba(24,16,28,0.5)] backdrop-blur-[8px] transition-[transform,box-shadow,border-color] duration-200 ease-sidebar hover:-translate-y-[3px] hover:border-[#A04A97]/40 hover:shadow-[0_2px_4px_rgba(24,16,28,0.05),0_18px_34px_-22px_rgba(160,74,151,0.55)] active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2 motion-reduce:animate-none motion-reduce:transition-none motion-reduce:hover:translate-y-0 animate-copilot-rise"
          >
            <span className="flex items-center justify-between gap-2">
              <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#A04A97]/10 text-[#A04A97] transition-transform duration-200 ease-sidebar group-hover/card:scale-105 motion-reduce:transition-none motion-reduce:group-hover/card:scale-100">
                {s.icon}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.09em] text-[#77707E]">
                {s.kind}
              </span>
            </span>
            <p className="m-0 text-[15px] font-medium leading-[1.4] text-[#18101C]">
              {s.label}
            </p>
            {/* The arrow appears only on hover — it answers "is this clickable?"
                at the moment the question is asked, and stays out of the way
                the rest of the time. */}
            <span className="flex translate-x-[-4px] items-center gap-1.5 text-[13px] font-semibold text-[#A04A97] opacity-0 transition-all duration-200 group-hover/card:translate-x-0 group-hover/card:opacity-100 group-focus-visible/card:opacity-100 motion-reduce:transition-none">
              Ask this
              <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </span>
          </button>
        ))}
      </div>

      <PickedForYou picks={picks} onOpen={onOpenPick} onSeeAll={onSeeAllPicks} />
    </div>
  );
};

export default CopilotEmptyState;
